/**
 * Utility functions for converting public R2 URLs to secure authenticated download URLs
 */

/**
 * Extract storage key from public R2 URL
 * Converts: https://assets.procrechesolutions.com/elearning/path/file.mp4
 * To: elearning/path/file.mp4
 */
export function extractStorageKeyFromPublicUrl(publicUrl: string): string | null {
  try {
    // Handle R2 public domain URLs
    if (publicUrl.includes('assets.procrechesolutions.com') || 
        publicUrl.includes('.r2.dev') ||
        publicUrl.includes('r2.cloudflarestorage.com')) {
      const url = new URL(publicUrl);
      const pathname = url.pathname.startsWith('/') ? url.pathname.substring(1) : url.pathname;
      const search = url.search;
      const hash = url.hash;
      const storageKey = `${pathname}${search}${hash}`;
      
      // Validate: reject path traversal attempts
      if (storageKey.includes('..')) {
        console.warn('Path traversal attempt detected:', publicUrl);
        return null;
      }
      
      return storageKey;
    }

    return null;
  } catch (error) {
    console.error('Error extracting storage key from URL:', error);
    return null;
  }
}

/**
 * Check if URL is a public R2 storage URL that should be converted to secure endpoint
 */
export function isPublicStorageUrl(url: string): boolean {
  if (!url) return false;
  
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.toLowerCase();
    
    // Check for known R2 public domains
    return (
      hostname === 'assets.procrechesolutions.com' ||
      hostname.endsWith('.r2.dev') ||
      hostname.endsWith('.r2.cloudflarestorage.com')
    );
  } catch {
    return false;
  }
}

/**
 * Convert public R2 URL to secure authenticated download URL
 * 
 * @param publicUrl - The public R2 URL
 * @returns Secure download URL that requires authentication
 * 
 * Example:
 * Input: https://assets.procrechesolutions.com/elearning/course/video.mp4
 * Output: /api/upload/download/elearning/course/video.mp4
 */
export function convertToSecureDownloadUrl(publicUrl: string): string {
  // If already a secure download URL, return as-is
  if (publicUrl.startsWith('/api/upload/download/')) {
    return publicUrl;
  }

  // If it's a full secure download URL, extract the path
  if (publicUrl.includes('/api/upload/download/')) {
    try {
      const url = new URL(publicUrl);
      return url.pathname; // Returns /api/upload/download/...
    } catch {
      // If not a valid URL, check if it contains the pattern
      const match = publicUrl.match(/\/api\/upload\/download\/.+$/);
      if (match) {
        return match[0];
      }
    }
  }

  // Extract storage key from public R2 URL
  const storageKey = extractStorageKeyFromPublicUrl(publicUrl);
  
  if (storageKey) {
    // Return secure download URL
    return `/api/upload/download/${storageKey}`;
  }

  // If we can't convert it, return the original URL
  // (might be external YouTube, Vimeo, etc.)
  return publicUrl;
}

/**
 * Convert fileUrl to secure format if needed
 * Handles multiple URL formats and returns the most secure version
 */
export function ensureSecureFileUrl(fileUrl: string | undefined | null): string {
  if (!fileUrl) return '';

  // Skip external video platforms
  const isExternalVideo = 
    fileUrl.includes('youtube.com') ||
    fileUrl.includes('youtu.be') ||
    fileUrl.includes('vimeo.com');
  
  if (isExternalVideo) {
    return fileUrl;
  }

  // Convert public R2 URLs to secure download URLs
  if (isPublicStorageUrl(fileUrl)) {
    return convertToSecureDownloadUrl(fileUrl);
  }

  // For relative paths or storage keys, assume they need secure endpoint
  if (!fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
    // Skip protocol-relative URLs and other schemes
    if (fileUrl.startsWith('//') || fileUrl.includes(':')) {
      return fileUrl;
    }
    
    // Remove leading slash to avoid double slashes
    const cleanPath = fileUrl.startsWith('/') ? fileUrl.substring(1) : fileUrl;
    return `/api/upload/download/${cleanPath}`;
  }

  // Return original for other cases
  return fileUrl;
}

